import type { MeetingDecisionItem, MeetingDetail } from '@fos/shared/contracts/meetings';
import { BookmarkPlus, Check } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { errorMessage } from '../../../lib/api';
import { plural } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import { FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Checkbox } from '../../../ui/Checkbox';
import { toast } from '../../../ui/Toast';

/**
 * „Ustalenia” w widoku spotkania (MVP §8: AI przygotowuje ustalenia, właściciel je przegląda; §10: propozycje
 * z transkrypcji → podgląd do zatwierdzenia). Ustalenia z AI to propozycje: zaznaczone trafiają do Wiedzy jako decyzje
 * ze źródłem w spotkaniu dopiero po „Zapisz w Wiedzy”; reszta może zostać odrzucona. Zapisane prowadzą do Wiedzy.
 * Wynik z adaptera fake jest oznaczony jako symulacja.
 */

const decisionsWord = (n: number) => plural(n, ['ustalenie', 'ustalenia', 'ustaleń']);

export function DecisionsBlock({ meeting }: { meeting: MeetingDetail }) {
  const access = useAccess();
  const utils = trpc.useUtils();
  const items = meeting.decisionItems;
  const pending = useMemo(() => items.filter((d) => d.status === 'proposed'), [items]);
  const saved = items.filter((d) => d.status === 'saved');
  const rejected = items.filter((d) => d.status === 'rejected');
  const canDecide = meeting.canDecideDecisions;
  const [selected, setSelected] = useState<Set<string>>(() => new Set(pending.map((d) => d.key)));
  const [error, setError] = useState<string | null>(null);
  const decide = trpc.meetings.decideDecisions.useMutation();

  // nowe propozycje (ponowne przetworzenie) domyślnie zaznaczone; rozstrzygnięte znikają z wyboru
  const known = useRef(new Set(pending.map((d) => d.key)));
  const pendingKey = pending.map((d) => d.key).join('|');
  // biome-ignore lint/correctness/useExhaustiveDependencies: tylko przy zmianie listy czekających ustaleń
  useEffect(() => {
    const fresh = pending.filter((d) => !known.current.has(d.key)).map((d) => d.key);
    for (const k of fresh) known.current.add(k);
    const keys = new Set(pending.map((d) => d.key));
    setSelected((prev) => new Set([...[...prev].filter((k) => keys.has(k)), ...fresh]));
  }, [pendingKey]);

  if (items.length === 0) return null;

  const chosen = pending.filter((d) => selected.has(d.key));
  const rest = pending.filter((d) => !selected.has(d.key));

  const run = async (save: string[], reject: string[], message: string) => {
    setError(null);
    try {
      await decide.mutateAsync({ meetingId: meeting.id, save, reject });
      await Promise.all([utils.meetings.invalidate(), utils.knowledge.invalidate()]);
      toast.success(message);
    } catch (err) {
      setError(errorMessage(err, 'Nie udało się zapisać decyzji.'));
    }
  };

  const toggle = (key: string, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(key);
      else next.delete(key);
      return next;
    });

  const linkFor = (d: MeetingDecisionItem) =>
    d.knowledgeId && access.can('knowledge') ? `/wiedza/${d.knowledgeId}` : null;

  return (
    <section aria-labelledby="ustalenia">
      <h2 id="ustalenia" className="text-body-s font-semibold text-fg">
        Ustalenia
      </h2>
      {pending.length > 0 && (
        <p className="mt-1 text-caption text-fg-muted">
          Propozycje AI{meeting.ai.simulated ? ' (symulacja, wynik z reguł)' : ''} — do Wiedzy trafią dopiero po
          zatwierdzeniu.
          {!canDecide && ' Zapisać je mogą osoby z edycją Wiedzy.'}
        </p>
      )}
      {error && (
        <div className="mt-2">
          <FormMessage>{error}</FormMessage>
        </div>
      )}
      {pending.length > 0 && (
        <ul aria-label="Ustalenia do przeglądu" className="mt-3 flex flex-col gap-2.5">
          {pending.map((d) => (
            <li key={d.key} className="flex items-start gap-2.5 text-body-s text-fg">
              {canDecide ? (
                <Checkbox
                  className="mt-0.5 items-start"
                  checked={selected.has(d.key)}
                  onCheckedChange={(on) => toggle(d.key, on)}
                  label={<span className="min-w-0 break-words">{d.text}</span>}
                />
              ) : (
                <>
                  <span aria-hidden className="mt-[0.65em] size-1.5 shrink-0 rounded-full bg-line-strong" />
                  <span className="min-w-0 break-words">{d.text}</span>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      {pending.length > 0 && canDecide && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button
            variant="secondary"
            size="s"
            icon={BookmarkPlus}
            disabled={chosen.length === 0 || decide.isPending}
            loading={decide.isPending}
            onClick={() =>
              void run(
                chosen.map((d) => d.key),
                [],
                `Zapisano w Wiedzy: ${chosen.length} ${decisionsWord(chosen.length)}`,
              )
            }
          >
            {chosen.length > 0 ? `Zapisz w Wiedzy (${chosen.length})` : 'Zapisz w Wiedzy'}
          </Button>
          {rest.length > 0 && (
            <Button
              variant="ghost"
              size="s"
              disabled={decide.isPending}
              onClick={() =>
                void run(
                  [],
                  rest.map((d) => d.key),
                  `Odrzucono ${rest.length} ${decisionsWord(rest.length)}`,
                )
              }
            >
              {chosen.length === 0 ? 'Odrzuć wszystkie' : 'Odrzuć resztę'}
            </Button>
          )}
        </div>
      )}
      {saved.length > 0 && (
        <div className={pending.length > 0 ? 'mt-4' : 'mt-3'}>
          <p className="text-caption font-medium text-fg-muted">Zapisane w Wiedzy</p>
          <ul aria-label="Ustalenia zapisane w Wiedzy" className="mt-1 flex flex-col gap-1">
            {saved.map((d) => {
              const to = linkFor(d);
              return (
                <li key={d.key} className="flex min-w-0 items-baseline gap-2 text-body-s">
                  <Check size={14} strokeWidth={1.75} aria-hidden className="shrink-0 self-center text-success" />
                  {to ? (
                    <Link to={to} className="min-w-0 break-words text-fg hover:underline">
                      {d.text}
                    </Link>
                  ) : (
                    <span className="min-w-0 break-words text-fg">{d.text}</span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {rejected.length > 0 && (
        <p className="mt-2 text-caption text-fg-muted">
          Odrzucone: {rejected.length} {decisionsWord(rejected.length)}
        </p>
      )}
    </section>
  );
}
